import React from "react";

const GridOverlay = ({
    containerId = "landing-container",
    colsMax = 48,
    rowsMax = 84,
    ratio = 1,
    cover = "full",
    show = true,
    majorEvery = 8,
    minorAlpha = 0.08,
    majorAlpha = 0.28,
    minorWidth = 1,
    majorWidth = 2,
    frameWidth = 2,
}) => {
    const [box, setBox] = React.useState({ w: 0, h: 0, padL: 0, padR: 0, padT: 0, padB: 0 });

    React.useEffect(() => {
        if (!show) return;
        const el = document.getElementById(containerId);
        if (!el) return;

        const update = () => {
            const r = el.getBoundingClientRect();
            const cs = getComputedStyle(el);
            setBox({
                w: r.width,
                h: r.height,
                padL: parseFloat(cs.paddingLeft) || 0,
                padR: parseFloat(cs.paddingRight) || 0,
                padT: parseFloat(cs.paddingTop) || 0,
                padB: parseFloat(cs.paddingBottom) || 0,
            });
        };

        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        window.addEventListener("resize", update, { passive: true });
        window.addEventListener("load", update);

        return () => {
            ro.disconnect();
            window.removeEventListener("resize", update);
            window.removeEventListener("load", update);
        };
    }, [containerId, show]);

    if (!show) return null;

    const innerW = Math.max(0, box.w - box.padL - box.padR);
    const innerH = Math.max(0, box.h - box.padT - box.padB);
    if (innerW === 0 || innerH === 0) return null;

    const colSize = innerW / colsMax;

    const gridHeight = innerW / ratio;
    const rowSize = gridHeight / rowsMax;

    const rowsEndY = box.padT + (cover === "full" ? innerH : gridHeight);

    const strokeMinor = `rgba(0,0,0,${minorAlpha})`;
    const strokeMajor = `rgba(0,0,0,${majorAlpha})`;
    const isMajor = (i) => i % majorEvery === 0;

    const rowsCount = Math.ceil((rowsEndY - box.padT) / rowSize);

    return (
        <svg width="100%" height="100%" style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 9999 }}>
            <rect
                x={box.padL + 0.5}
                y={box.padT + 0.5}
                width={innerW - 1}
                height={(cover === "full" ? innerH : gridHeight) - 1}
                fill="none"
                stroke={strokeMajor}
                strokeWidth={frameWidth}
                shapeRendering="crispEdges"
            />

            {Array.from({ length: colsMax + 1 }).map((_, i) => {
                const x = box.padL + i * colSize + 0.5;
                return (
                    <line
                        key={`v${i}`}
                        x1={x}
                        y1={box.padT}
                        x2={x}
                        y2={rowsEndY}
                        stroke={isMajor(i) ? strokeMajor : strokeMinor}
                        strokeWidth={isMajor(i) ? majorWidth : minorWidth}
                        shapeRendering="crispEdges"
                    />
                );
            })}
            {Array.from({ length: rowsCount + 1 }).map((_, i) => {
                const y = box.padT + i * rowSize + 0.5;
                return (
                    <line
                        key={`h${i}`}
                        x1={box.padL}
                        y1={y}
                        x2={box.padL + innerW}
                        y2={y}
                        stroke={isMajor(i) ? strokeMajor : strokeMinor}
                        strokeWidth={isMajor(i) ? majorWidth : minorWidth}
                        shapeRendering="crispEdges"
                    />
                );
            })}
        </svg>
    );
};

export const WidgetDebugOutline = ({ grid, w, h }) => {
    if (!grid) return null;
    const cols = grid?.cols?.size ? Math.round(w / grid.cols.size) : 0;
    const rows = grid?.rows?.size ? Math.round(h / grid.rows.size) : 0;

    return (
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 9998 }}>
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    outline: "2px dashed rgba(0,0,0,.35)",
                    outlineOffset: "-2px",
                    borderRadius: 4,
                }}
            />
            <div
                style={{
                    position: "absolute",
                    top: 4,
                    right: 6,
                    padding: "2px 6px",
                    fontSize: 11,
                    lineHeight: 1,
                    borderRadius: 10,
                    background: "rgba(0,0,0,.6)",
                    color: "#fff",
                }}
            >
                {cols}×{rows}
            </div>
        </div>
    );
};

export default GridOverlay;
